import React, { useEffect, useRef } from "react"
import { rgbToHex } from "../utils"

const density = 5

const Canvas = React.memo(function Canvas({ action, imageData, scale, translate }) {
  const randomnessDataRef = useRef([])

  useEffect(() => {
    if (!action.points) return // not a brush action
    const data = randomnessDataRef.current

    function getRandomFloat(min, max) {
      return Math.random() * (max - min) + min;
    }

    for (let p = 0; p < action.points.length - randomnessDataRef.current.length; p++) {
      let pointData = []

      for (let d = 0; d < density; d++) {
        const angle = getRandomFloat(0, Math.PI * 2)
        const radius = getRandomFloat(0, 50)
        const alpha = Math.random()

        pointData.push({ angle, radius, alpha })
      }

      data.push(pointData)
    }

    randomnessDataRef.current = data
  }, [action.points])

  function fill(elem) {
    const { width, height } = imageData
    const context = elem.getContext('2d')

    const img = new Image()
    img.onload = () => {
      paint(elem, [action], [255, 0, 0, 255])

      const ptrn = context.createPattern(img, 'repeat');

      context.save()
      context.globalCompositeOperation = 'source-in'
      context.fillStyle = ptrn
      context.fillRect(0, 0, width, height)
      context.restore()
    }

    img.setAttribute('src', action.colorUrl)
  }

  function paint(elem, actions, _colorArr) {
    let { width, height } = imageData
    let colorsImgData = elem.getContext("2d").createImageData(width, height)
    let res = colorsImgData.data

    actions.forEach(action => {
      const { colorArr, mask } = action
      if (mask === null) return

      let x, y;
      let { data, bounds, width: maskW } = mask

      for (y = bounds.minY; y <= bounds.maxY; y++) {
        for (x = bounds.minX; x <= bounds.maxX; x++) {
          if (data[y * maskW + x] === 0) continue
          let k = (y * width + x) * 4
          res[k] = (_colorArr || colorArr)[0]
          res[k + 1] = (_colorArr || colorArr)[1]
          res[k + 2] = (_colorArr || colorArr)[2]
          res[k + 3] = (_colorArr || colorArr)[3]
        }
      }
    })

    elem.getContext("2d").putImageData(colorsImgData, 0, 0)
  }

  function draw(elem) {
    if (action.points.length === 0) return

    const ctx = elem.getContext('2d')
    ctx.clearRect(0, 0, imageData.width, imageData.height)

    if (action.isSolidColor) {
      if (action.isSpray) {
        _spray(action.colorHex)
      } else {
        _stroke(action.colorHex)
      }
    } else {
      const img = new Image()
      img.onload = () => {
        const ptrn = ctx.createPattern(img, 'repeat')

        if (action.isSpray) {
          _spray(ptrn)
        } else {
          _stroke(ptrn)
        }
      }

      img.setAttribute('src', action.colorUrl)
    }

    function _stroke(strokeStyle) {
      ctx.strokeStyle = strokeStyle
      ctx.lineJoin = ctx.lineCap = 'round';
      ctx.lineWidth = action.lineWidth;
      ctx.beginPath();
      ctx.moveTo(action.points[0].x, action.points[0].y);

      for (let i = 1; i < action.points.length; i++) {
        ctx.lineTo(action.points[i].x, action.points[i].y);
      }

      ctx.stroke()
    }

    function _spray(fillStyle) {
      ctx.lineJoin = ctx.lineCap = 'round';

      for (let i = 0; i < action.points.length; i++) {
        for (let d = 0; d < density; d++) {
          // Not optimal, but it works
          if (randomnessDataRef.current[i] && randomnessDataRef.current[i][d]) {
            const { angle, radius, alpha } = randomnessDataRef.current[i][d]

            ctx.globalAlpha = alpha
            ctx.fillStyle = fillStyle

            ctx.fillRect(
              action.points[i].x + radius * Math.cos(angle),
              action.points[i].y + radius * Math.sin(angle),
              5, 5
            )
          }
        }
      }
    }
  }

  function doIt(elem) {
    // TODO: prevent rerendering (elem = null?)
    if (!elem) return

    if (action.usingBrush) {
      return draw(elem)
    }

    if (action.type === 'paint') {
      paint(elem, action.actions)
    } else {
      fill(elem)
    }
  }

  return <canvas
    ref={doIt}
    width={imageData.width}
    height={imageData.height}
    style={{ transform: `scale(${scale})`  }}
  />
})

export function CanvasGroup({ actions, requestingImgDataUrl, boundingBox, pickPoint, onColorPick, onImgDataUrlReady, imageData, scale, translate, scaleUpRatio }) {
  useEffect(() => {
    if (pickPoint.x !== null && pickPoint.y !== null) {
      onColorPick(getColorAtPoint(pickPoint))
    }
  }, [pickPoint])

  useEffect(() => {
    if (actions.length === 0) return

    if (requestingImgDataUrl) {
      const img = document.getElementById('raw-big-picture')
      const mergedCanvas = mergeCanvases(img)
      const dataURL = mergedCanvas.toDataURL('image/jpeg', 1.0)

      onImgDataUrlReady(dataURL)
    }
  }, [requestingImgDataUrl])

  function getColorAtPoint({ x, y }) {
    if (actions.length === 0) return

    const mergedCanvas = mergeCanvases()
    const [r, g, b] = mergedCanvas.getContext('2d').getImageData(x, y, 1, 1).data

    return rgbToHex(r, g, b)
  }

  function mergeCanvases(svgImg) {
    if (actions.length === 0) return

    const { width, height } = imageData
    const canvasesArr = Array.from(document.querySelectorAll('#canvas-container canvas'))
    const rawImageCanvas = document.createElement("canvas")

    rawImageCanvas.getContext("2d").canvas.width = width
    rawImageCanvas.getContext("2d").canvas.height = height

    const resultCanvas = canvasesArr.reduce((acc, crt) => {
      acc.getContext('2d').drawImage(crt, 0, 0)

      return acc
    }, rawImageCanvas)

    if (svgImg) {
      rawImageCanvas.getContext("2d").drawImage(svgImg, 0, 0, width, height)
    }

    return resultCanvas
  }

  function optimizeActions() {
    let paintActions = []
    const _actions = []

    for (let i = 0; i < actions.length; i++) {
      const action = actions[i]

      if (!action.usingBrush && action.isSolidColor) {
        paintActions.push(action)

        if (i === actions.length - 1) {
          _actions.push({ type: 'paint', actions: [...paintActions] })
        }
      } else {
        if (paintActions.length) {
          _actions.push({ type: 'paint', actions: [...paintActions] })
          paintActions = []
        }

        _actions.push(action)
      }
    }

    return _actions
  }

  return <div id="canvas-container" style={{ transform: `translate(${translate.x}px, ${translate.y}px)  scale(${1 / scaleUpRatio})` }}>
    {optimizeActions().map((action, idx) => <Canvas key={idx} action={action} imageData={imageData} boundingBox={boundingBox} scale={scale} translate={translate} />)}
  </div>
}
